import * as React from "react";

export interface IHelpDrawerContext {
    isOpen: boolean;
    hasOpened: boolean;
    setIsOpen: (open: boolean) => void;
    topicKey: string | null;
    routeTopicKey: string | null;
    navigationMessage?: string;
    navigating: boolean;
    selectTopic: (key: string) => number;
    isCurrentSelection: (sequence: number) => boolean;
    finishNavigation: (sequence: number, message?: string) => void;
    prepareNavigation: (sequence: number) => number;
    syncRoute: (key: string | null, navigationToken?: number) => void;
    showRouteHelp: () => void;
    cancelNavigation: () => void;
}
const HelpDrawerContext = React.createContext<IHelpDrawerContext | undefined>(undefined);
export const useHelpDrawer = (): IHelpDrawerContext => {
    const context = React.useContext(HelpDrawerContext);
    if (!context) throw new Error("useHelpDrawer must be used within HelpDrawerProvider");
    return context;
};
export const HelpDrawerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isOpen, setOpen] = React.useState(false);
    const [hasOpened, setHasOpened] = React.useState(false);
    const setIsOpen = React.useCallback((open: boolean) => {
        if (open) {
            setHasOpened(true);
        }
        setOpen(open);
    }, []);
    const [routeTopicKey, setRouteTopicKey] = React.useState<string | null>(null);
    const [selectedTopicKey, setSelectedTopicKey] = React.useState<string | null>(null);
    const [navigationMessage, setNavigationMessage] = React.useState<string>();
    const [navigating, setNavigating] = React.useState(false);
    const sequence = React.useRef(0);
    const pending = React.useRef<number | undefined>(undefined);
    const cancelNavigation = React.useCallback(() => {
        sequence.current++;
        pending.current = undefined;
        setNavigating(false);
    }, []);
    const showRouteHelp = React.useCallback(() => {
        cancelNavigation();
        setSelectedTopicKey(null);
        setNavigationMessage(undefined);
    }, [cancelNavigation]);
    const syncRoute = React.useCallback((key: string | null, token?: number) => {
        setRouteTopicKey(key);
        if (token === undefined || pending.current !== token) showRouteHelp();
        pending.current = undefined;
    }, [showRouteHelp]);
    const selectTopic = React.useCallback((key: string) => {
        pending.current = undefined;
        setSelectedTopicKey(key);
        setNavigationMessage(undefined);
        setNavigating(true);
        return ++sequence.current;
    }, []);
    const isCurrentSelection = React.useCallback((token: number) => sequence.current === token, []);
    const finishNavigation = React.useCallback((token: number, message?: string) => {
        if (sequence.current !== token) return;
        setNavigating(false);
        setNavigationMessage(message);
    }, []);
    const prepareNavigation = React.useCallback((token: number) => {
        pending.current = token;
        return token;
    }, []);
    const value = React.useMemo(() => ({
        isOpen, hasOpened, setIsOpen, topicKey: selectedTopicKey ?? routeTopicKey, routeTopicKey,
        navigationMessage, navigating, selectTopic, isCurrentSelection, finishNavigation,
        prepareNavigation, syncRoute, showRouteHelp, cancelNavigation,
    }), [isOpen, hasOpened, setIsOpen, selectedTopicKey, routeTopicKey, navigationMessage, navigating, selectTopic,
        isCurrentSelection, finishNavigation, prepareNavigation, syncRoute, showRouteHelp, cancelNavigation]);
    return <HelpDrawerContext.Provider value={value}>{children}</HelpDrawerContext.Provider>;
};
export default HelpDrawerContext;
